import firebase from 'firebase/app';
import { useEffect, useState } from 'react';

import { firestore } from '../../../firebase/firebase';
import { WantedListing, WantedListingFormData } from '../../../models/wantedListings.model';

export const useWantedListings = (): {
  listings: WantedListing[];
  loading: boolean;
  error: string | null;
} => {
  const [listings, setListings] = useState<WantedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = firestore
      .collection('wantedListings')
      .where('status', '==', 'active')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as WantedListing[];

          setListings(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching wanted listings:', err);
          setError(err.message);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, []);

  return { listings, loading, error };
};

export const useAddWantedListing = (): {
  addListing: (data: Omit<WantedListing, 'id' | 'createdAt' | 'status'>) => Promise<string>;
  loading: boolean;
  error: string | null;
} => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addListing = async (data: Omit<WantedListing, 'id' | 'createdAt' | 'status'>) => {
    setLoading(true);
    setError(null);

    try {
      const newListing: Omit<WantedListing, 'id'> = {
        ...data,
        status: 'active',
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      };

      const docRef = await firestore.collection('wantedListings').add(newListing);

      setLoading(false);
      return docRef.id;
    } catch (err) {
      console.error('Error adding wanted listing:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
      throw err;
    }
  };

  return { addListing, loading, error };
};

export const useEditWantedListing = (): {
  editListing: (listingId: string, data: Partial<WantedListingFormData>) => Promise<void>;
  loading: boolean;
  error: string | null;
} => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editListing = async (listingId: string, data: Partial<WantedListingFormData>) => {
    setLoading(true);
    setError(null);

    try {
      await firestore
        .collection('wantedListings')
        .doc(listingId)
        .update({
          ...data,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

      setLoading(false);
    } catch (err) {
      console.error('Error editing wanted listing:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
      throw err;
    }
  };

  return { editListing, loading, error };
};
